import { ServiceRepresentation } from '../../../../../../components/dataRepresentation/ServiceRepresentation';
import { CustomFormattedMessage } from '../../../../../../i18n/i18n';
import { useServiceConfigurationFormContext } from '../ServiceConfigurationForm';
import {
  PaymentMethodState,
  PaymentPartnerType,
  ServiceType,
} from '../../../../../../codegen/vehicles';
import Select from '@rio-cloud/rio-uikit/Select';
import { ServiceCardRepresentation } from '../../../../../../components/dataRepresentation/ServiceCardRepresentation';
import { MaskedCardNumber } from '../../../../../../components/MaskedCardNumber';
import { FormField } from '../../../../../../components/form/FormField';
import { Controller } from 'react-hook-form';
import isEmpty from 'lodash/isEmpty';
import { DIALOG_DATA_COLOR } from '../ServiceConfigurationDialog';
import { UserManualLink } from '../../../../../../components/dialog/userManualDialog/UserManualLink';
import {
  userManualDialogChapters,
  UserManualDialogChaptersKeys,
} from '../../../../../../state/dialogs/chapters';
import { PaymentMethod } from '../../../../../../models';

// ServiceType.Other has no manual chapter, so no link is shown for it.
const userManualChapters: Partial<
  Record<ServiceType, UserManualDialogChaptersKeys>
> = {
  [ServiceType.Fueling]: userManualDialogChapters.servicesFuelling,
  [ServiceType.Parking]: userManualDialogChapters.servicesParking,
  [ServiceType.Washing]: userManualDialogChapters.servicesWashing,
  [ServiceType.Breakdown]:
    userManualDialogChapters.servicesRepairAndMaintenance,
  [ServiceType.Charging]: userManualDialogChapters.servicesCharging,
};

type ServiceCardSelectorProps = {
  serviceType: ServiceType;
};

export const ServiceCardSelector = ({
  serviceType,
}: ServiceCardSelectorProps) => {
  const { control, getPaymentMethods } = useServiceConfigurationFormContext();
  const userManualChapter = userManualChapters[serviceType];

  const serviceCardOptions = [
    {
      id: '',
      label: (
        <div className={'display-flex justify-content-end'}>
          <CustomFormattedMessage id={'onboarding.dialog.serviceCards.none'} />
        </div>
      ),
    },
  ];

  // If more payment partner restrictions are added, consider extracting this
  // into a declarative "service eligibility matrix" (e.g. Record<PaymentPartnerType, ServiceType[]>)
  const paymentMethodIsAvailableForService = (paymentMethod: PaymentMethod) => {
    if (
      paymentMethod.paymentPartner === PaymentPartnerType.ManChargeAndGoPayment
    ) {
      return serviceType === ServiceType.Charging;
    }
    if (paymentMethod.paymentPartner === PaymentPartnerType.DkvPayment) {
      return serviceType === ServiceType.Fueling;
    }
    return true;
  };

  getPaymentMethods()
    .filter((pm) => pm.state === PaymentMethodState.Active)
    .filter((pm) => paymentMethodIsAvailableForService(pm))
    .forEach((pm) => {
      serviceCardOptions.push({
        id: pm.id,
        label: (
          <div className={'display-flex justify-content-between'}>
            <ServiceCardRepresentation paymentPartner={pm.paymentPartner} />
            <div className={'margin-left-20'}>
              <MaskedCardNumber lastFourDigits={pm.lastFourDigits} />
            </div>
          </div>
        ),
      });
    });

  return (
    <div
      className={'margin-top-20'}
      data-testid={`onboarding-dialog-body-service-${serviceType}`}
    >
      <div className={'panel panel-default margin-bottom-0'}>
        <div className="panel-body padding-bottom-0">
          <div className={'display-flex align-items-center'}>
            <div className={`${DIALOG_DATA_COLOR} text-size-20`}>
              <ServiceRepresentation
                serviceType={serviceType}
                representationSize="large"
              />
            </div>
            {userManualChapter && (
              <div className={'margin-left-5'}>
                <UserManualLink
                  chapter={userManualChapter}
                  dataTestId={`user-manual-link-service-${serviceType}`}
                />
              </div>
            )}
          </div>
        </div>
        <div
          className={'margin-10'}
          data-testid={'onboarding-dialog-body-serviceCardTypeSection'}
        >
          {serviceCardOptions.length > 1 ? (
            <Controller
              name={`serviceActivations.${serviceType}.paymentMethodAssignments`}
              render={({ field: { onChange, value } }) => (
                <FormField>
                  <Select
                    aria-labelledby="card-type-label"
                    placeholder={
                      <CustomFormattedMessage
                        id={
                          'onboarding.dialog.paymentMethodDetails.selectCardTypePlaceholder'
                        }
                      />
                    }
                    options={serviceCardOptions}
                    onChange={(e) => {
                      onChange(
                        isEmpty(e?.id)
                          ? []
                          : [
                              {
                                paymentMethodId: e?.id,
                              },
                            ],
                      );
                    }}
                    value={isEmpty(value) ? [] : [value[0].paymentMethodId]}
                  />
                </FormField>
              )}
              control={control}
              rules={{
                required: true,
              }}
            />
          ) : (
            <span className="padding-left-10 text-italic text-color-dark">
              <CustomFormattedMessage
                id={'onboarding.dialog.serviceCards.noCardAvailableForService'}
              />
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
